import clsx from "clsx";
import React from "react";
import {
  Drawer,
  DrawerProps,
  IconButton,
  Box,
  ClickAwayListener,
  Portal,
} from "@mui/material";

import { ReactComponent as CloseIcon } from "../../icons/Close.svg";
import { ReactComponent as RemoveIcon } from "../../icons/Bin.svg";

import { AdminExerciseAssetDto } from "@growth-machine-llc/stridist-api-client";
import { useAdminPublicDeleteExercise } from "./hooks/useAdminPublicDeleteExercise";
import {
  ExerciseEditingType,
  ManageLibraryExercise,
} from "../exercise-library/ManageLibraryExercise";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { exerciseDrawerStyles } from "../exercise-library/exerciseDrawerStyles";

export interface WorkoutDrawerProps extends DrawerProps {
  exerciseAsset?: AdminExerciseAssetDto;
  onUpdate?: (workout: AdminExerciseAssetDto) => void;
  onCancel?: () => void;
  onRemove?: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    exercise?: AdminExerciseAssetDto,
  ) => void;
  onSelectExercise?: (exercise: AdminExerciseAssetDto) => void;
}

export function AdminWorkoutDrawer(props: WorkoutDrawerProps) {
  const {
    className,
    exerciseAsset,
    onUpdate,
    onCancel,
    onRemove,
    onSelectExercise,
    ...other
  } = props;
  const { onClose } = props;
  const s = exerciseDrawerStyles();

  const { mutate: deleteExercise } = useAdminPublicDeleteExercise();

  const [confirmExerciseDeleteDialogOpen, setConfirmExerciseDeleteDialogOpen] =
    React.useState(false);

  const handleConfirmExerciseDelete = React.useCallback(
    (event) => {
      deleteExercise(exerciseAsset.id);
      if (onClose) {
        onClose(event, "backdropClick");
      }
    },
    [exerciseAsset, deleteExercise],
  );

  const handleClose = React.useCallback(
    (event) => {
      onClose(event, "backdropClick");
    },
    [onClose],
  );

  const handleClickAway = React.useCallback(
    (event) => {
      const el = event.target as HTMLDivElement;
      if (el && "closest" in el) {
        const withinMainRoot = el.closest("#root");

        if (withinMainRoot) {
          handleClose(event);
        }
      }
    },
    [handleClose],
  );

  return (
    <Portal>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Drawer
          className={clsx(s.root, className)}
          classes={{ paper: s.paper }}
          anchor="right"
          variant="persistent"
          {...other}
        >
          <Box className={s.buttons}>
            <Box>
              {/* Use only for deleting public exercises */}
              {!exerciseAsset?.coachId && (
                <IconButton
                  className={clsx(s.headerButton, s.deleteButton)}
                  onClick={() => setConfirmExerciseDeleteDialogOpen(true)}
                  size="large"
                >
                  <RemoveIcon />
                </IconButton>
              )}
              <IconButton
                className={clsx(s.headerButton, s.closeButton)}
                onClick={handleClose}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <ManageLibraryExercise
            key={exerciseAsset?.id}
            exerciseAsset={exerciseAsset}
            onSelectExercise={onSelectExercise}
            onClose={handleClose}
            createNewAsset={!exerciseAsset}
            editingType={ExerciseEditingType.PUBLIC}
            allowChangeExerciseDuringRename={true}
          />
        </Drawer>
      </ClickAwayListener>
      <ConfirmActionDialog
        title={"Are you sure you want to delete exercise?"}
        open={confirmExerciseDeleteDialogOpen}
        onClose={() => setConfirmExerciseDeleteDialogOpen(false)}
        onCancel={() => setConfirmExerciseDeleteDialogOpen(false)}
        onConfirm={handleConfirmExerciseDelete}
      />
    </Portal>
  );
}
